import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  pipelineConfigSuffix,
  unscopeDocumentIdFromPipelineConfig,
} from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

type PipelineConfigEntry = {
  entry: {
    id: string
    data?: { status?: string; name?: string | null }
  }
}

/** Whether a document exists under one specific pipeline config for a site. */
export interface ConfigAvailability {
  configId: string
  status: string | undefined
  isAvailable: boolean
}

export interface DocumentConfigAvailability {
  bareId: string
  providedConfigId: string | undefined
  perConfig: ConfigAvailability[]
  availableConfigs: ConfigAvailability[]
  isLoading: boolean
}

/**
 * Probes which of a site's pipeline configs a document is available under:
 * one entry per bare `DOC-<hash>` id, belonging to whichever config its
 * `pipeline-config` tag names (no tag → `'legacy'`). Ids using the retired
 * `_<configSuffix>` convention are first reduced to their bare form. Pass
 * `documentId: undefined` to disable the probe entirely.
 */
export function useDocumentConfigAvailability(
  documentId: string | undefined,
  siteID: string | undefined
): DocumentConfigAvailability {
  const { data: configs, isLoading: isLoadingConfigs } =
    useGetRolodexEntriesByLoadOptions<PipelineConfigEntry[]>(
      documentId && siteID
        ? { type: ['pipeline-config'], tags: { site: siteID }, limit: 50 }
        : undefined
    )

  const bareId = useMemo(() => {
    if (!documentId) return ''
    return (configs ?? []).reduce(
      (id, config) => unscopeDocumentIdFromPipelineConfig(id, config.entry.id),
      documentId
    )
  }, [documentId, configs])

  const providedConfigId = useMemo(() => {
    if (!documentId) return undefined
    return (configs ?? []).find((config) =>
      documentId.endsWith(`_${pipelineConfigSuffix(config.entry.id)}`)
    )?.entry.id
  }, [documentId, configs])

  const { data: docs, isLoading: isLoadingDocs } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      bareId
        ? {
            type: [RolodexEntryType.SYSTEM_DOCUMENT],
            ids: [bareId],
            limit: 1,
          }
        : undefined
    )

  return useMemo(() => {
    const entry = (docs ?? []).find((wrapper) => wrapper.entry.id === bareId)
    const entryConfigTag =
      entry && (!siteID || entry.entry.tags['site'] === siteID)
        ? (entry.entry.tags['pipeline-config'] ?? 'legacy')
        : undefined

    const perConfig: ConfigAvailability[] = (configs ?? []).map((config) => ({
      configId: config.entry.id,
      status: config.entry.data?.status,
      isAvailable: entryConfigTag === config.entry.id,
    }))

    if (entryConfigTag === 'legacy') {
      perConfig.push({
        configId: 'legacy',
        status: undefined,
        isAvailable: true,
      })
    }

    return {
      bareId,
      providedConfigId,
      perConfig,
      availableConfigs: perConfig.filter((row) => row.isAvailable),
      isLoading: isLoadingConfigs || isLoadingDocs,
    }
  }, [
    configs,
    docs,
    bareId,
    siteID,
    providedConfigId,
    isLoadingConfigs,
    isLoadingDocs,
  ])
}
